import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { loadAnalytics, trackPageView } from '../utils/analytics';

const STORAGE_KEY = 'cookieConsent'; // 'accepted' | 'declined'
const isPrerender = typeof navigator !== 'undefined' && navigator.userAgent === 'ReactSnap';

export default function CookieConsent() {
  const location = useLocation();
  const [consent, setConsent] = useState(() =>
    typeof window !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null
  );
  const [visible, setVisible] = useState(false);

  // On mount: honour a prior choice, or (for new visitors) show the banner.
  // Never render the banner during react-snap prerender — it would bake into every static page.
  useEffect(() => {
    if (isPrerender) return;
    if (consent === 'accepted') {
      loadAnalytics();
    } else if (consent == null) {
      const t = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fire a page_view on every route change — only once consent is granted.
  useEffect(() => {
    if (consent === 'accepted') trackPageView(location.pathname + location.search);
  }, [location, consent]);

  const accept = () => {
    localStorage.setItem(STORAGE_KEY, 'accepted');
    setConsent('accepted');
    setVisible(false);
    loadAnalytics();
    trackPageView(location.pathname + location.search);
  };

  const decline = () => {
    localStorage.setItem(STORAGE_KEY, 'declined');
    setConsent('declined');
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[60] p-4 md:p-6"
      role="dialog"
      aria-label="Cookie consent"
      aria-live="polite"
    >
      <div className="max-w-4xl mx-auto bg-cafe-noir text-eggshell rounded-2xl border border-eggshell/10 p-5 md:p-6 flex flex-col md:flex-row md:items-center gap-4">
        <p className="text-sm leading-relaxed flex-1">
          We use cookies to understand how our site is used and improve your experience. Accept
          analytics cookies, or continue with only the essentials.{' '}
          <Link to="/terms" className="underline hover:text-hunyadi-yellow">Learn more</Link>.
        </p>
        <div className="flex gap-3 shrink-0">
          <button
            onClick={decline}
            className="px-5 py-2.5 rounded-full border border-eggshell/40 text-eggshell hover:bg-eggshell/10 transition-colors text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-eggshell"
          >
            Essential only
          </button>
          <button
            onClick={accept}
            className="px-5 py-2.5 rounded-full bg-hunyadi-yellow text-cafe-noir hover:bg-brown-sugar hover:text-white transition-colors text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-hunyadi-yellow"
          >
            Accept all
          </button>
        </div>
      </div>
    </div>
  );
}
